import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { GraduationCap, RotateCcw, CheckCircle2, XCircle } from "lucide-react";
import { Card } from "./shared";

function Question({ q, qi, picked, result, onPick }) {
    return (
        <div data-testid={`quiz-question-${qi}`}>
            <p className="text-sm font-medium mb-2">{qi + 1}. {q.q}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {q.options.map((o, oi) => {
                    const isCorrect = result && result.correct[qi] === oi;
                    const isWrong = result && picked === oi && !isCorrect;
                    const tone = isCorrect ? "border-ares-emerald/60 bg-ares-emerald/10" : isWrong ? "border-destructive/60 bg-destructive/10"
                        : picked === oi ? "border-ares-orange/60 bg-ares-orange/10" : "border-ares-border bg-ares-panel/60 hover:border-ares-orange/30";
                    return (
                        <button key={oi} disabled={!!result} onClick={() => onPick(oi)}
                            className={`text-left text-sm rounded-md border px-3 py-2 transition-colors ${tone}`} data-testid={`quiz-q${qi}-opt-${oi}`}>
                            {o}
                        </button>
                    );
                })}
            </div>
            {result && <p className="text-xs text-ares-muted mt-2" data-testid={`quiz-why-${qi}`}>{result.explanations[qi]}</p>}
        </div>
    );
}

export default function DisciplineQuiz({ discipline, progress, onDone }) {
    const [answers, setAnswers] = useState({});
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const quiz = discipline.quiz;
    const complete = quiz.every((_, i) => answers[i] !== undefined);

    const submit = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/ground-quiz/${discipline.id}`, { answers: quiz.map((_, i) => answers[i]) });
            setResult(data);
            onDone && onDone();
            toast.success(`You scored ${data.score}/${data.total}`);
        } catch (e) {
            toast.error("Could not submit quiz");
        } finally {
            setBusy(false);
        }
    };
    const retry = () => { setAnswers({}); setResult(null); };

    return (
        <Card title={`${discipline.name} self-check`} icon={GraduationCap} accent="text-ares-amber" testid="discipline-quiz-card">
            {progress && (
                <p className="text-xs text-ares-muted -mt-2 mb-4" data-testid="quiz-best-score">
                    Best score {progress.best}/{progress.total} · {progress.attempts} attempt{progress.attempts === 1 ? "" : "s"}
                </p>
            )}
            <div className="space-y-5">
                {quiz.map((q, qi) => (
                    <Question key={qi} q={q} qi={qi} picked={answers[qi]} result={result} onPick={(oi) => setAnswers((a) => ({ ...a, [qi]: oi }))} />
                ))}
            </div>
            <div className="flex items-center gap-3 mt-5">
                {!result ? (
                    <Button onClick={submit} disabled={!complete || busy} className="bg-ares-amber hover:bg-ares-amber/80 text-ares-void font-semibold disabled:opacity-75 disabled:bg-ares-panel disabled:text-ares-muted" data-testid="quiz-submit-btn">
                        Check answers
                    </Button>
                ) : (
                    <>
                        <span className={`flex items-center gap-1.5 text-sm font-semibold ${result.score === result.total ? "text-ares-emerald" : "text-ares-amber"}`} data-testid="quiz-score">
                            {result.score === result.total ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                            {result.score}/{result.total} correct{result.score === result.total ? " · mastered" : ""}
                        </span>
                        <Button variant="outline" onClick={retry} className="ml-auto h-9 border-ares-border bg-transparent" data-testid="quiz-retry-btn">
                            <RotateCcw className="w-4 h-4 mr-2" /> Try again
                        </Button>
                    </>
                )}
            </div>
        </Card>
    );
}
